import { motion } from "framer-motion";
import { Wheat, Cake, Coffee, Award, Clock, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { fadeInUp, staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

interface Product {
  id: string;
  nameAr: string;
  descriptionAr: string;
  image: string;
}

const breadProducts: Product[] = [
  {
    id: "bread-1",
    nameAr: "خبز فرنسي طازج",
    descriptionAr: "خبز فرنسي مقرمش من الخارج وطري من الداخل، يُخبز يومياً بأجود أنواع الدقيق",
    image: '/images/WhatsApp Image 2026-05-05 at 1.31.10 PM (3).jpeg',
  },
  {
    id: "bread-2",
    nameAr: "خبز الحبوب الكاملة",
    descriptionAr: "خبز صحي غني بالألياف والمعادن، مثالي للإفطار الصحي",
    image: '/images/whole_grain_bread_1 (1)_page-0001 (1).jpg',
  },
  {
    id: "bread-3",
    nameAr: "كرواسون فرنسي",
    descriptionAr: "كرواسون بالزبدة الطبيعية، طبقات هشة ونكهة غنية",
    image: IMAGES.CAFE_SUPPLY_1,
  },
  {
    id: "bread-4",
    nameAr: "خبز الشوفان",
    descriptionAr: "خبز طري بالشوفان والعسل، خيار مثالي للصحة والطعم",
    image: '/images/oat_bread_1 (1)_page-0001.jpg',
  },
  {
    id: "bread-5",
    nameAr: "بريوش محلى",
    descriptionAr: "بريوش فرنسي طري بالزبدة والبيض، مثالي للإفطار الفاخر",
    image: '/images/brioche_bread_1 (1)_page-0001 (1).jpg',
  },
  {
    id: "bread-6",
    nameAr: "خبز الزيتون",
    descriptionAr: "خبز إيطالي بزيت الزيتون والأعشاب الطبيعية",
    image: '/images/olive_bread_1 (1)_page-0001.jpg',
  },
];

const dessertProducts: Product[] = [
  {
    id: "dessert-1",
    nameAr: "تشيز كيك فاخر",
    descriptionAr: "تشيز كيك كريمي بقاعدة البسكويت وطبقة التوت الطازج",
    image: IMAGES.DESSERTS_1,
  },
  {
    id: "dessert-2",
    nameAr: "تارت الفواكه الموسمية",
    descriptionAr: "كيك غني بالشوكولاتة البلجيكية الفاخرة مع كريمة الجاناش",
    image: IMAGES.DESSERTS_2,
  },
  {
    id: "dessert-3",
    nameAr: "تارت الفواكه الموسمية",
    descriptionAr: "تارت بالكريمة والفواكه الطازجة، تحفة فنية بنكهة استثنائية",
    image: IMAGES.DESSERTS_5,
  },
  {
    id: "dessert-4",
    nameAr: "ماكرون فرنسي",
    descriptionAr: "ماكرون بنكهات متنوعة، قوام مثالي وطعم لا يُقاوم",
    image: IMAGES.DESSERTS_6,
  },
  {
    id: "dessert-5",
    nameAr: "إكلير بالشوكولاتة",
    descriptionAr: "إكلير فرنسي محشو بكريمة الفانيليا ومغطى بالشوكولاتة",
    image: IMAGES.DESSERTS_7,
  },
  {
    id: "dessert-6",
    nameAr: "تيراميسو إيطالي",
    descriptionAr: "تيراميسو أصلي بالقهوة الإيطالية وجبنة الماسكاربوني",
    image: IMAGES.DESSERTS_9,
  },
];

const coffeeBreakProducts: Product[] = [
  {
    id: "coffee-1",
    nameAr: "بوكس كوفي بريك متكامل",
    descriptionAr: "تشكيلة متنوعة من المخبوزات والحلويات مع القهوة الفاخرة",
    image: IMAGES.COFFEE_POUR_1,
  },
  {
    id: "coffee-2",
    nameAr: "ميني باستري مشكل",
    descriptionAr: "تشكيلة من الباستري الصغير المثالي للاجتماعات",
    image: IMAGES.DESSERTS_10,
  },
  {
    id: "coffee-3",
    nameAr: "كوكيز فاخر",
    descriptionAr: "كوكيز طازج بالشوكولاتة والمكسرات",
    image: '/images/ChatGPT Image 10 مايو 2026، 04_43_33 ص.png',
  },
];

const qualityStandards = [
  {
    icon: Award,
    titleAr: "مكونات فاخرة",
    descriptionAr: "نستخدم أجود أنواع الدقيق والزبدة والشوكولاتة المستوردة",
  },
  {
    icon: Clock,
    titleAr: "طازج يومياً",
    descriptionAr: "جميع منتجاتنا تُخبز يومياً لضمان أعلى درجات الطازجية",
  },
  {
    icon: ShieldCheck,
    titleAr: "معايير صحية صارمة",
    descriptionAr: "نلتزم بأعلى معايير السلامة الغذائية والجودة",
  },
];

const galleryImages = [
  IMAGES.DESSERTS_5,
  '/images/WhatsApp Image 2026-05-05 at 1.31.09 PM (3).jpeg',
  IMAGES.CAFE_SUPPLY_1,
  IMAGES.COFFEE_POUR_1,
  IMAGES.DESSERTS_1,
  IMAGES.HOSPITALITY_TABLE_10,
];

export default function BakeryDesserts() {
  return (
    <div className="min-h-screen bg-background" dir="rtl">
        <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.DESSERTS_5}
              alt="مخبوزات وحلويات"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          </div>

          <motion.div
            className="relative z-10 text-center px-4 max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6">
              نخبز بالشغف، نحلو بالإتقان
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8">
              تشكيلة واسعة من المخبوزات الطازجة والحلويات الفاخرة
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              اطلب الآن
            </Button>
          </motion.div>
        </section>

        <section className="py-20 px-4">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                منتجاتنا
              </h2>
              <p className="text-xl text-muted-foreground">
                اختر من تشكيلتنا الواسعة من المخبوزات والحلويات
              </p>
            </motion.div>

            <Tabs defaultValue="bread" className="w-full" dir="rtl">
              <TabsList className="grid w-full grid-cols-3 mb-12 bg-muted">
                <TabsTrigger value="bread" className="text-lg">
                  <Wheat className="ml-2 h-5 w-5" />
                  خبز ومخبوزات
                </TabsTrigger>
                <TabsTrigger value="desserts" className="text-lg">
                  <Cake className="ml-2 h-5 w-5" />
                  حلويات
                </TabsTrigger>
                <TabsTrigger value="coffee" className="text-lg">
                  <Coffee className="ml-2 h-5 w-5" />
                  كوفي بريك
                </TabsTrigger>
              </TabsList>

              <TabsContent value="bread">
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {breadProducts.map((product) => (
                    <motion.div
                      key={product.id}
                      variants={staggerItem}
                      className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.nameAr}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-2xl font-semibold text-foreground mb-3">
                          {product.nameAr}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {product.descriptionAr}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </TabsContent>

              <TabsContent value="desserts">
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {dessertProducts.map((product) => (
                    <motion.div
                      key={product.id}
                      variants={staggerItem}
                      className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.nameAr}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-2xl font-semibold text-foreground mb-3">
                          {product.nameAr}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {product.descriptionAr}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </TabsContent>

              <TabsContent value="coffee">
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {coffeeBreakProducts.map((product) => (
                    <motion.div
                      key={product.id}
                      variants={staggerItem}
                      className="bg-card rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.nameAr}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-2xl font-semibold text-foreground mb-3">
                          {product.nameAr}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {product.descriptionAr}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="py-20 px-4 bg-muted">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                معايير الجودة
              </h2>
              <p className="text-xl text-muted-foreground">
                نلتزم بأعلى معايير الجودة في كل منتج
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {qualityStandards.map((standard, index) => {
                const Icon = standard.icon;
                return (
                  <motion.div
                    key={index}
                    variants={staggerItem}
                    className="bg-card rounded-xl p-8 text-center shadow-sm"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
                      <Icon className="h-8 w-8 text-accent" />
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4">
                      {standard.titleAr}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {standard.descriptionAr}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        <section className="py-20 px-4 bg-accent/5">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                كيفية الطلب
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                نوفر خدمة التوصيل اليومي للكافيهات والمطاعم والشركات
              </p>
            </motion.div>

            <motion.div
              className="bg-card rounded-2xl p-8 md:p-12 shadow-sm max-w-3xl mx-auto"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="space-y-6 text-right">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      تواصل معنا
                    </h3>
                    <p className="text-muted-foreground">
                      اتصل بنا أو أرسل رسالة واتساب لمناقشة احتياجاتك
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      حدد المنتجات والكميات
                    </h3>
                    <p className="text-muted-foreground">
                      اختر من تشكيلتنا الواسعة وحدد الكميات المطلوبة
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      استلم طلبك طازجاً
                    </h3>
                    <p className="text-muted-foreground">
                      نوصل لك المنتجات طازجة في الوقت المحدد
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 text-center">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  اطلب عرض سعر
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 px-4">
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                معرض الصور
              </h2>
              <p className="text-xl text-muted-foreground">
                شاهد بعضاً من إبداعاتنا
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {galleryImages.map((image, index) => (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  className="aspect-square overflow-hidden rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <img
                    src={image}
                    alt={`معرض ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </div>
  );
}
